import { useEffect, useState } from "react";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

const shareTargets = [
  { key: "copy", label: "复制链接", icon: "ri-link", desc: "复制后粘贴给好友" },
  { key: "wechat", label: "微信", icon: "ri-wechat-line", desc: "分享到聊天或朋友圈" },
  { key: "weibo", label: "微博", icon: "ri-weibo-line", desc: "发布一条微博动态" },
  { key: "qzone", label: "QQ空间", icon: "ri-qq-line", desc: "分享到空间动态" }
];

export default function ShareDialog({ open, onClose, title }: ShareDialogProps) {
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!open) setFeedback("");
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleShare = async (key: string, label: string) => {
    if (key === "copy") {
      const url = window.location.href;
      try {
        await navigator.clipboard.writeText(url);
        setFeedback("链接已复制到剪贴板（演示）");
      } catch {
        setFeedback("复制失败，请手动复制地址栏链接（演示）");
      }
      return;
    }
    setFeedback(`已唤起「${label}」分享面板（演示，未接入真实服务）`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-background-950/80 p-0 backdrop-blur-sm animate-fade-in sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="分享"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-lg border border-background-700 bg-background-900 p-6 animate-scale-in sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-600 text-foreground-50">
              分享影视
            </h3>
            <p className="mt-1 line-clamp-1 text-sm text-foreground-400">
              {title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-300 transition-colors hover:bg-background-800 hover:text-accent-400"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {shareTargets.map((target) => (
            <button
              key={target.key}
              type="button"
              onClick={() => handleShare(target.key, target.label)}
              className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-background-700 bg-background-950/50 px-3 py-4 text-center transition-colors hover:border-accent-500"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-800 text-accent-400">
                <i className={`${target.icon} text-xl`} aria-hidden="true" />
              </span>
              <span className="text-sm font-500 text-foreground-100">
                {target.label}
              </span>
            </button>
          ))}
        </div>

        {feedback && (
          <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-500/15 px-3 py-2 text-xs text-secondary-200">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-information-line" aria-hidden="true" />
            </span>
            {feedback}
          </p>
        )}

        <p className="mt-4 text-center text-xs text-foreground-500">
          仅供演示，未接入真实分享服务
        </p>
      </div>
    </div>
  );
}